import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as IconCheckSquare } from 'bootstrap-icons/icons/check-square.svg'
import { ReactComponent as IconFileEarmarkFill } from 'bootstrap-icons/icons/file-earmark-fill.svg'
import { ReactComponent as IconInfoCircle } from 'bootstrap-icons/icons/info-circle.svg'
import Textarea from "react-textarea-autosize";
import SignatureCanvas from 'react-signature-canvas'
import { Link, useLocation } from '@reach/router'
import { canResourceAccessPrescriptions, canResourceAccessOrders } from '../../../../utils/prescriptionRestriction';
import { getResourceWarningTexts } from '../getResourceWarningTexts';

import useUpload from "hooks/useUpload";
import Resource from './Resource'
import Consumer from './Consumer'
import Vademecum from '../Vademecum/Vademecum'
import Treatment from './Treatment'
import useStore from './useStore'
import useCanvasResize from './useCanvasResize'
import getDate from 'lib/getDate'
import useQuery from "hooks/useQuery";
import useMutation from 'hooks/useMutation'
import useTeamID from "hooks/useTeamID";


let {
  REACT_APP_API_URL,
  REACT_APP_PRESCRIPTION_FRONT_URL,
  REACT_APP_API_KEY,
} = process.env

const clone = window.structuredClone || JSON.clone

function Prescription(props) {
  let upload = useUpload()
  let _location = useLocation()
  let [preview, setPreview] = useState()
  let $canvas = useRef(null)
  let $logo = useRef(null)
  let { t } = useTranslation();
  let [type, setType] = useState(props.type || 'prescription')
  let [whatsapp, setWhatsApp] = useState({})
  let [email, setEmail] = useStore(state => [state.email, state.setEmail])
  let [size, setSize] = useStore(state => [state.size, state.setSize])
  let [copies, setCopies] = useStore(state => [state.copies, state.setCopies])
  let [response, setResponse] = useState()
  let [current, setCurrent] = useState([])
  let [showCurrentTreatments, setShowCurrentTreatments] = useState(false)
  let [showCertificateForm, setShowCertificateForm] = useState(false)
  let [showCanvasSignature, setShowCanvasSignature] = useState(false)
  let [signature, setSignature] = useState({ base64: '' })
  let [certificate, setCertificate] = useState({})
  let [logo, setLogo] = useState({ flag: window.localStorage.getItem('includeLogoFlag') === "true" ? true : false })
  let [consumer, setConsumer] = useState()
  let [resource, setResource] = useState({})
  let [warnings, setWarnings] = useState({})

  let [checkingProducts, setCheckingProducts] = useState(false)
  let [mutate, mutation] = useMutation(`prescriptions/${window._l('').substr(1)}`, { includeApiKey: true });
  let [notifyByEmail, notifyByEmailMutation] = useMutation(`prescriptions/email`, { includeApiKey: true });
  let [saveLogo, saveLogoMutation] = useMutation(`prescriptions/${window._l('').substr(1)}/logos`, { includeApiKey: true });
  let [deleteLogo, deleteLogoMutation] = useMutation(`prescriptions/${window._l('').substr(1)}/logos/delete`, { includeApiKey: true });

  let [financier, setFinancier] = useState({})
  let [specialty, setSpecialty] = useState({})
  let [license, setLicense] = useState({})
  let [location, setLocation] = useState({})
  let [identification, setIdentification] = useState({})
  let [showConsumerForm, setShowConsumerForm] = useState()
  let [consumerWarnings, setConsumerWarnings] = useState()
  let [resourceWarnings, setResourceWarnings] = useState({})

  let [state, setState] = useState({
    indications: "",
    diagnostic: "",
    content: "",
    date: getDate(),
    treatments: [],
  })

  const checkProductActive = async (product_code, productInfo) => {
    if (productInfo.status === "done") return false;
    if (!product_code) return true

    try {
      const response = await fetch(`${REACT_APP_API_URL}/vademecums/ar/alfabeta/${product_code}`)
      let result = true;
      if (response.ok) {
        let product = null
        try {
          product = await response.json()
        } catch (e) {
          result = false
        }
        result = product && product.active;
      }
      return result;
    } catch (error) {
      console.error('Error verificando producto:', error)
      return false;
    }
  }

  const checkMultipleProductsActive = async (productsToCheck) => {
    if (!productsToCheck.length) return []

    let verificationResults = []
    if (productsToCheck.length > 0) {
      const batchSize = 10 // Aumentado de 5 a 10
      const batches = []

      for (let i = 0; i < productsToCheck.length; i += batchSize) {
        batches.push(productsToCheck.slice(i, i + batchSize))
      }

      // Procesar todos los lotes en paralelo en lugar de secuencialmente
      const allBatchPromises = batches.map(async (batch) => {
        const batchPromises = batch.map(async ({ product_code, item }) => {
          const isActive = await checkProductActive(product_code, item)
          return { product_code, item, isActive }
        })

        return Promise.all(batchPromises)
      })

      // Esperar a que todos los lotes se completen
      const allBatchResults = await Promise.all(allBatchPromises)

      // Aplanar los resultados
      verificationResults = allBatchResults.flat()
    }

    return verificationResults
  }

  const query = useQuery(
    window._l(`/${consumer?.id}/treatments`).substr(1),
    {
      enabled: !!consumer?.id,
      onSuccess: async items => {
        if (!Array.isArray(items)) return setCurrent([])
        setCheckingProducts(true)

        try {
          // Filtrar y preparar productos en una sola pasada
          const productsToCheck = []
          const seenProducts = new Set()

          for (let item of items) {
            if (!/^records\//.test(item.id)) continue
            if (item.deleted) continue
            if (!item.label && !item.product && !item.drug) continue
            if (item.status === "done") continue

            let product_code = item.product_code || `custom:${item.drug || item.label || item.product}`
            if (!product_code) continue
            // Los tratamientos custom (texto libre, fuera del vademécum) no deben ofrecerse
            // como medicamentos seleccionables al generar una receta (card 934)
            if (product_code.startsWith('custom:')) continue
            if (seenProducts.has(product_code)) continue
            if (state.treatments?.some(t => t.product_code === product_code && !t.optional)) continue

            seenProducts.add(product_code)
            productsToCheck.push({ product_code, item })
          }

          // Verificar productos del vademécum en paralelo
          let verifiedPayloads = []
          if (productsToCheck.length > 0) {
            const results = await checkMultipleProductsActive(productsToCheck)
            // Crear payloads solo para productos activos
            verifiedPayloads = results
              .filter(({ isActive }) => isActive)
              .map(({ product_code, item }) => ({
                product_code,
                product: item.product,
                label: item.label,
                drugs: item.drugs || [],
                presentation: item.presentation,
                drug: item.drug,
                company: item.company,
                quantity: 1,
                optional: true,
                isLong: false,
                noSubstitute: false,
                generic: false,
              }))
          }

          setCurrent(verifiedPayloads)

        } catch (error) {
          console.error('Error procesando tratamientos:', error)
          setCurrent([])
        } finally {
          setCheckingProducts(false)
        }
      }
    }
  );

  const team = useQuery(useTeamID(), {
    enabled: !props.team,
  });

  let logos = useQuery(`prescriptions/${window._l('').substr(1)}/logos`, {
    onSuccess: data => {
      if (!Array.isArray(data)) return
      let logo = [...data].reverse().at(0)
      if (!logo?.url) return

      logo.flag = window.localStorage.getItem('includeLogoFlag') === "true" ? true : false;
      setLogo({ ...logo })
    }
  })

  const onChangeFinancier = financier => {
    setFinancier(financier)
  }

  let onSaveConsumer = consumer => {
    setConsumer(clone(consumer))
    if (showConsumerForm) setShowConsumerForm(false)
  }

  const onChange = ({ target: { name, value } }) => {
    state[name] = value
    setState(clone(state))
  }

  const onClose = () => {
    if (!consumerWarnings?.flag && showConsumerForm) return setShowConsumerForm(false)
    if (props.onClose) return props.onClose()
  }

  const onChangeTreatment = treatment => {
    if (treatment.remove) {
      state.treatments = state.treatments.filter(t => t.product_code !== treatment.product_code)
    } else if (treatment.add) {
      if (!state.treatments.some(t => t.product_code === treatment.product_code)) {
        state.treatments.push({ ...treatment, add: undefined, added: true })
      }
    } else {
      for (let i = 0; i < state.treatments.length; i += 1) {
        let item = state.treatments[i]
        if (item.product_code !== treatment.product_code) continue
        state.treatments[i] = { ...item, ...treatment }
      }
    }
    setState(clone(state))
  }

  const onVademecumChange = treatments => {
    if (!treatments) return

    let treatment = treatments[0]
    if (!treatment) return

    if (!Array.isArray(state.treatments)) {
      state.treatments = []
    }

    if (state.treatments.some(t => t.product_code === treatment.product_code)) return

    state.treatments.push(treatment)
    setState(clone(state))
  }

  let onChangeLicense = license => {
    setLicense(license)
  }

  let onChangeIdentification = identification => {
    setIdentification(identification)
  }

  let onChangeLocation = location => {
    setLocation(location)
  }

  let onChangeSpecialty = specialty => {
    setSpecialty(specialty)
  }

  let getSignatureKey = () => {
    let uid = props.user?.uid
    if (!uid) throw new Error('missing_uid')

    return ['ehr/signature', uid].filter(Boolean).join(':')
  }

  let getCertificateKey = () => {
    let uid = props.user?.uid
    if (!uid) throw new Error('missing_uid')

    return ['ehr/certificate', uid].filter(Boolean).join(':')
  }

  let storeCertificate = certificate => {
    try {
      let storeKey = getCertificateKey()
      let payload = JSON.stringify({
        name: certificate?.name,
        base64: certificate?.base64,
        type: certificate?.type,
      })
      window.localStorage.setItem(storeKey, payload)
    } catch (e) {
      console.error(e)
    }
  }

  let loadCertificate = () => {
    try {
      let storeKey = getCertificateKey()
      let json = window.localStorage.getItem(storeKey)
      let certificate = JSON.parse(json)
      if (certificate && certificate.base64 && certificate.name && certificate.type) {
        setCertificate(certificate)
        setShowCertificateForm(true)
      }

    } catch (e) {
      console.error(e)
    }
  }

  let loadSignature = () => {
    try {
      let storeKey = getSignatureKey()
      let base64 = window.localStorage.getItem(storeKey)
      if (base64 && $canvas.current) {
        setShowCanvasSignature(true)
        $canvas.current.fromDataURL(base64, { ratio: 1, width: 630, height: 150 })
      }
      if (base64) setSignature({ base64, canvas: 'hide' })
      return base64
    } catch (e) {
      console.error(e)
    }
  }

  let onChangeLogo = async ({ target: { name, value, type, files } }) => {
    let payload = clone(logo)
    let file = files?.[0]
    if (type === 'file' && file) {
      setLogo({ ...logo, uploading: true })
      let links = await upload(files)
      let url = links?.[0]
      if (url) {
        payload.url = url
        await saveLogo({ url, resourceID: resource?.id })
      }

    } else if (type === 'checkbox') {
      payload[name] = !payload[name]
    } else {
      payload[name] = value
    }

    if (name === 'flag' && payload.flag && !payload.url) try {
      $logo.current.click()
    } catch (e) {
      console.log(e)
    }

    setLogo(payload)
  }

  let onClearLogo = () => {
    let confirmAction = window.confirm("¿Está seguro que quiere borrar el logo?");
    if (!confirmAction) return;

    $logo.current.value = ''
    deleteLogo({ urls: [logo.url] });
    setLogo({ ...logo, url: undefined })
  }

  const onChangeCertificate = async ({ target: { name, value, type, files } }) => {
    let payload = clone(certificate)
    let file = files?.[0]
    if (type === 'file' && file) {
      payload.name = file.name
      const { type, base64 } = await new Promise(resolve => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        // reader.onload = () => resolve(reader.result.replace('data:application/x-pkcs12;base64,',''))
        reader.onload = () => {
          try {
            let [type, base64] = reader.result.split(';')
            base64 = base64.replace('base64,', '')
            type = type.replace('data:', '')
            return resolve({ type, base64 })
          } catch {
            resolve({})
          }
        }
      })
      payload.base64 = base64
      payload.type = type
      storeCertificate(payload)
    } else if (type === 'checkbox') {
      payload[name] = !payload[name]
    } else {
      payload[name] = value
    }

    setCertificate(payload)
  }

  let onUpdateSignature = () => {
    try {
      let base64 = $canvas.current.toDataURL()
      let key = getSignatureKey()
      setSignature({ ...signature, base64 })
      window.localStorage.setItem(key, base64)
    } catch { }
  }

  let onClearSignature = () => {
    try {
      $canvas.current.clear()
      setSignature({ canvas: 'show' })
    } catch (e) { }
    try {
      let key = getSignatureKey()
      window.localStorage.removeItem(key)
    } catch { }
  }

  // Ver useCanvasResize.js — iguala la resolución real del canvas de firma
  // al tamaño CSS ya resuelto en cada resize, para que el trazo no quede
  // desplazado de donde tocás en un celular más angosto que los 630px fijos
  // de canvasProps.
  useCanvasResize($canvas, showCanvasSignature && signature.canvas !== 'hide')

  let makePayload = () => {
    const identificationTable = {
      'dni': 'DNI',
      'pasaporte': 'PA',
      'ced': 'CED',
      'le': 'LE'
    }


    let payload = {
      qrEndpoint: REACT_APP_PRESCRIPTION_FRONT_URL,
      size,
      copies: parseInt(copies) || 1,
      signature: showCanvasSignature ? signature?.base64 : '',
      date: state.date,
      indications: state.indications,
      content: state.content,
      diagnostic: state.diagnostic || '',
      certificate: showCertificateForm && certificate?.name ? certificate : undefined,
      tags: state.tags,
      logo: logo?.flag && logo?.url ? logo.url : undefined,
      source: 'drapp'
    }

    if (state.treatments.length > 0) {
      payload.treatments = state.treatments
    }

    if (resource?.id) {
      let address = location?.address || ''
      let emails = resource?.emails?.map(e => e.value)?.filter(Boolean) || []
      if (Array.isArray(location?.emails)) {
        emails.push(...location.emails)
      }
      payload.resource = {
        id: resource.id,
        label: resource.label,
        givenName: resource.firstName,
        familyName: resource.lastName,
        dob: resource.dob,
        gender: resource.gender === "other" ? "X" : resource.gender,
        address,
        emails,
        phones: location?.phones || [],
        specialty: (() => { const s = specialty?.id ? t(specialty.id, specialty.label) : ''; return s.length > 100 ? s.slice(0, 97) + '...' : s; })(),
      }
    }

    if (payload.resource && license?.value) {
      payload.resource.license = {
        number: license.value,
        kind: license.kind,
        jurisdiction: license.jurisdiction
      }
    }

    if (payload.resource && identification?.value) {
      payload.resource.identification = {
        value: identification.value,
        type: "DNI"
      }
    }

    if (consumer?.id) {
      payload.consumer = {
        id: consumer.id,
        label: consumer.label,
        identification: {
          value: consumer.identification,
          type: consumer.identificationType ? (identificationTable[consumer.identificationType] || 'DNI') : 'DNI'
        },
        dob: consumer.dob,
        gender: consumer.gender === "other" ? "X" : consumer.gender,
        emails: [email.address].filter(Boolean),
        address: consumer.address,
      }
    }

    if (payload.consumer && financier?.id) {
      let [name, ...rest] = financier.id?.replace('pms_financiers:', '').split('/').filter(Boolean)
      let plan = rest.join('')
      if (plan === '*') {
        plan = ''
      }
      payload.consumer.financier = {
        key: financier.id || financier.key,
        label: financier.label || t(financier.id, financier.label),
        plan: financier.plan || plan || '',
        identification: financier.identification
      }
    } else if (consumer?.financiers?.length <= 0 && !financier?.id) {
      payload.consumer.financier = {
        key: "pms_financiers:private",
        label: "Particular",
        plan: ""
      }
    }
    return payload
  }

  let getPreview = async () => {
    if (!_location.search.includes('preview')) return
    let payload = makePayload()
    if (!payload.resource?.id || !payload.consumer?.id) return setPreview()
    if (!payload.resource?.license?.number) return setPreview()

    if (!payload.content && warnings.indications && (!payload.treatments || payload.treatments.length <= 0)) return setPreview()
    if (!payload.content && payload.treatments && payload.treatments.length > 0 && warnings.diagnostic) return setPreview()

    let buffer = await fetch(`${REACT_APP_API_URL}/prescriptions/preview`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(REACT_APP_API_KEY && { 'x-api-key': REACT_APP_API_KEY })
      },
      body: JSON.stringify(payload)
    }).then(res => res.blob())

    let blob = new Blob([buffer], { type: 'application/pdf' });
    setPreview(window.URL.createObjectURL(blob))
  }

  let sendEmail = async () => {
    let payload = {
      resource: { emails: [], label: resource.label },
      consumer: { emails: [email.address].filter(Boolean), label: consumer.label },
      ...response
    }
    if (email.cc1 && Array.isArray(resource?.emails) && resource.emails.length > 0) {
      payload.resource.emails = resource.emails?.map(e => e.value)?.filter(Boolean) || []
    }
    if (email.cc2 && Array.isArray(location?.emails) && location.emails.length > 0) {
      payload.resource.emails.push(...location.emails)
    }

    notifyByEmail(payload, {
      onMutate: () => {
        setEmail({ ...email, status: 'sending' })
      },
      onSuccess: () => {
        setEmail({ ...email, status: 'sent' })
      },
      // Bug real (auditoría 2026-08-14): sin onError, un envío de email
      // fallido dejaba el status pegado en 'sending' para siempre — el
      // input y el botón quedan deshabilitados (disabled={!!email.status})
      // sin ningún aviso y sin forma de reintentar salvo abandonar toda la
      // pantalla de éxito.
      onError: error => {
        setEmail({ ...email, status: undefined })
        window.notify({
          message: error.message,
          type: 'danger',
          title: 'Error',
        })
      }
    })
  }

  let reset = (type, flag) => {
    setType(type || 'prescription')
    setEmail({ ...email, status: undefined })
    setResponse()
    if (!flag) {
      setState({
        indications: "",
        content: "",
        diagnostic: "",
        date: getDate(),
        treatments: [],
      })
      setSignature({ base64: '' })
      setShowCanvasSignature(false)
      setTimeout(() => {
        if (props.user?.uid) {
          loadSignature()
        }
      }, 100)
    }
  }

  const save = () => {
    setResponse()
    if (response?.pdf && !response?.status?.code) return

    let payload = makePayload()

    mutate(payload, {
      onSuccess: response => {
        let options = { pdf: response.pdf }
        options.consumer = consumer?.label?.split(',')?.reverse()?.at(0)?.trim()
        options.resource = resource?.label?.split(', ')?.reverse()?.join(' ')
        let text = t('ehr:prescription_whatsapp_message', `Hola ${options.consumer},\nTe paso el link con la receta.\n${options.resource}\n${options.pdf}`, options)
        let params = new URLSearchParams({ text })
        params = params.toString()
        setResponse({ ...response })
        setWhatsApp({ ...whatsapp, params })
        if (props.onSave) props.onSave()
      },
      // Bug real (auditoría 2026-08-14): sin onError, si la creación de la
      // receta fallaba (red, 500, rechazo de validación) mutation.isLoading
      // simplemente volvía a false y el botón se reabilitaba en silencio —
      // el texto de error de más abajo (response?.status?.description) solo
      // se pinta dentro de una respuesta EXITOSA, nunca ante un fallo real
      // de la mutación. El profesional no tenía ninguna señal de que no se
      // generó nada.
      onError: error => {
        window.notify({
          message: error.message,
          type: 'danger',
          title: 'Error',
        })
      }
    })
  }

  useEffect(() => {
    try {
      if (props.user?.uid) loadCertificate()
    } catch { }
  }, [props.user])

  useEffect(() => {
    try {
      if (props.user?.uid) loadSignature()
    } catch { }
  }, [props.user])

  useEffect(() => {
    if (consumer && consumer.id) try {
      let address = consumer.emails.find(email => email.value)?.value || ''
      let number = consumer.phones.find(phone => phone.value)?.value || ''
      setEmail({ address, status: undefined })
      setWhatsApp({ number })
    } catch { }
  }, [consumer])


  useEffect(() => {
    try {
      let warnings = {}
      if (type === 'prescription') {
        warnings.treatments = state.treatments.length <= 0
        warnings.indications = state.indications.trim().length <= 7
        warnings.diagnostic = state.diagnostic.trim().length <= 2
      } else if (type !== 'prescription') {
        warnings.content = state.content.replace(/\s+/g, '').length <= 2
      }
      setWarnings(payload => ({ ...payload, ...warnings }))
    } catch (e) {
      console.error(e)
    }
  }, [type, state])

  useEffect(() => {
    if (props.consumer?.id) setConsumer(clone(props.consumer))
  }, [props.consumer])

  useEffect(() => {
    setResource(clone(props.resource))
  }, [props.resource])

  useEffect(() => {
    window.localStorage.setItem('includeLogoFlag', !!logo.flag);
  }, [logo])

  useEffect(() => {
    getPreview()
  }, [_location.search, consumer, email, whatsapp, resource, state, signature, license, financier, specialty, location, copies, showCanvasSignature, certificate, showCertificateForm])

  let _treatments = (state.treatments || []).filter(t => !t.optional || !showCurrentTreatments)
  let isInvalidFinancier = consumerWarnings?.financiers && consumerWarnings?.financiers[financier?.id]?.flag;

  const isCustomFinancier = financier?.customOption;

  if (!canResourceAccessPrescriptions(team?.data, resource) && type === 'prescription') {
    return (
      <div className="position-fixed top-0 start-0 end-0 bottom-0 w-100 h-100 overflow-auto d-flex align-items-center justify-content-center" style={{ zIndex: 1020, background: 'rgba(0, 0, 0, 0.5)' }}>
        <div className="bg-white rounded shadow-lg p-4 mx-3" style={{ maxWidth: '500px', width: '100%' }}>
          <div className="text-center">
            <div className="mb-3">
              <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
            </div>
            <h4 className="mb-3 text-dark">{t('prescription_restricted_title', 'Módulo de Recetas No Disponible')}</h4>
            <p className="mb-4 text-muted">
              {t('prescription_restriction_message', 'No tenés este profesional verificado para crear recetas. Contactate con nuestro equipo de soporte para obtener mas información.')}
            </p>
            <button
              onClick={() => onClose()}
              className="btn btn-primary"
            >
              {t('close', 'Cerrar')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (!canResourceAccessOrders(team?.data, resource) && type === 'order') {
    return (
      <div className="position-fixed top-0 start-0 end-0 bottom-0 w-100 h-100 overflow-auto d-flex align-items-center justify-content-center" style={{ zIndex: 1020, background: 'rgba(0, 0, 0, 0.5)' }}>
        <div className="bg-white rounded shadow-lg p-4 mx-3" style={{ maxWidth: '500px', width: '100%' }}>
          <div className="text-center">
            <div className="mb-3">
              <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
            </div>
            <h4 className="mb-3 text-dark">{t('order_restricted_title', 'Módulo de Órdenes No Disponible')}</h4>
            <p className="mb-4 text-muted">
              {t('order_restriction_message', 'No tenés este profesional verificado para crear órdenes. Contactate con nuestro equipo de soporte para obtener mas información.')}
            </p>
            <button
              onClick={() => onClose()}
              className="btn btn-primary"
            >
              {t('close', 'Cerrar')}
            </button>
          </div>
        </div>
      </div>
    );
  }
  if (resourceWarnings?.flag) {
    const warningTranslations = getResourceWarningTexts(resourceWarnings, t);
    const warningText = warningTranslations.length > 1
      ? warningTranslations.slice(0, -1).join(', ') + ' y ' + warningTranslations[warningTranslations.length - 1]
      : warningTranslations.join(', ');

    return (
      <div className={props.className || ''}>
        <div style={{ maxWidth: '680px' }} className="d-flex flex-fill flex-column gap-2 bg-white rounded my-3 mx-auto p-3">
          <div className="bg-warning p-2 rounded">
            {t('ehr:prescription_warnings_required_fields', 'Debes completar tu {{warningsText}} para poder generar recetas', { context: 'form', warningsText: warningText })}
          </div>
          {/* Bug real (auditoría 2026-08-18, responsive): sin flex-wrap, con 3-4
              warnings simultáneos (típico en un profesional recién dado de
              alta sin matrícula/consultorio/especialidad/cobertura) la fila
              supera los ~343px disponibles a 375px -- el overflow-x:hidden
              global recorta "Mas tarde", el único botón para cerrar esta
              pantalla. */}
          <div className="d-flex align-items-center flex-wrap gap-3">
            {(resourceWarnings?.license) && (
              <Link onClick={() => onClose()} to={window._l(`/setup/${resource?.id}/docs`)}>{t('ehr:prescription_warnings_link_to_docs_resource_setup', 'Matriculas')}</Link>
            )}
            {(resourceWarnings?.location) && (
              <Link onClick={() => onClose()} to={window._l(`/setup/${resource?.id}/ranges`)}>{t('ehr:prescription_warnings_link_to_locations_resource_setup', 'Consultorios')}</Link>
            )}
            {(resourceWarnings?.specialty) && (
              <Link onClick={() => onClose()} to={window._l(`/setup/${resource?.id}/practices`)}>{t('ehr:prescription_warnings_link_to_services_resource_setup', 'Especialidades')}</Link>
            )}
            {(resourceWarnings?.financier) && (
              <Link onClick={() => onClose()} to={window._l(`/setup/${resource?.id}/financiers`)}>{t('ehr:prescription_warnings_link_to_financiers_resource_setup', 'Coberturas')}</Link>
            )}
            {(resourceWarnings?.identification) && (
              <Link onClick={() => onClose()} to={window._l(`/setup/${resource?.id}/docs`)}>{t('ehr:prescription_warnings_link_to_dni_resource_setup', 'Configurar DNI')}</Link>
            )}
            {(resourceWarnings?.dob) && (
              <Link onClick={() => onClose()} to={window._l(`/setup/${resource?.id}/profile`)}>{t('ehr:prescription_warnings_link_to_dob_resource_setup', 'Configurar Fecha de Nacimiento')}</Link>
            )}
            {(resourceWarnings?.gender) && (
              <Link onClick={() => onClose()} to={window._l(`/setup/${resource?.id}/profile`)}>{t('ehr:prescription_warnings_link_to_gender_resource_setup', 'Configurar Genero')}</Link>
            )}
            <div className="flex-fill"></div>
            <button className="btn btn-sm text-secondary" onClick={onClose}>{t('ehr:prescription_warnings_resources_setup_later', 'Mas tarde')}</button>
          </div>
        </div>
      </div>
    );
  }

  return showConsumerForm || consumerWarnings?.flag || isInvalidFinancier || (consumer && !consumer.id) ? (
    <div className={props.className || ''}>
      <div style={{ maxWidth: '680px' }} className="d-flex flex-fill flex-column gap-2 bg-white rounded my-3 mx-auto p-3">
        {consumerWarnings && (consumerWarnings.identification || consumerWarnings?.financiers?.flag || consumerWarnings?.firstName || consumerWarnings?.lastName || consumerWarnings?.gender || consumerWarnings?.dob) && (
          <div className="bg-warning p-2 rounded">
            {t('ehr:prescription_warnings_missing_consumer_info', 'Para realizar una receta son necesarios datos minimos del paciente, como: Nombre, Apellido, DNI. En caso que no sea por particular y tiene una cobertura medica tambien es necesario indicar el nro. de afiliado. A continuacion marcamos los datos que faltan', { context: 'form' })}
          </div>
        )}
        <Consumer.Form
          modal={false}
          consumerID={consumer.id}
          initialState={consumer}
          onClose={() => onClose()}
          onSave={onSaveConsumer}
          remove={false}
          ehr={false}
          events={false}
          warnings={{
            identification: consumerWarnings?.identification,
            identification_length: consumerWarnings?.identification_length,
            financiers: consumerWarnings?.financiers,
            firstName: consumerWarnings?.firstName,
            lastName: consumerWarnings?.lastName,
            gender: consumerWarnings?.gender,
            dob: consumerWarnings?.dob,
          }}
          source="prescription"
        />
      </div>
    </div>
  ) : response && response.pdf && !response.status?.code ? (
    <div className={props.className || ''}>
      <div className="d-flex justify-content-center gap-2 mt-3">
        <div style={{ maxWidth: '680px' }} className="bg-white rounded over p-3">

          <h5 className=" fw-light text-center mb-3">
            {t('ehr:prescription_created_title', 'Receta Creada')}
          </h5>

          <a href={response?.pdf} rel="noopener noreferrer" target="_blank" className="btn bg-light text-primary border text-start mb-3">
            <div>{t('ehr:open_or_download_pdf', 'Abrir o descargar receta en una nueva pestaña', { context: 'form' })}</div>
            <small className="text-secondary">{t('ehr:open_pdf_hint', 'Por la manera que funcionan los navegadores en algunos casos el PDF se abre directamente en el navegador y en otras se solicita descargar')}</small>
          </a>

          {/* Bug real (auditoría 2026-08-24, responsive): sin flex-wrap, el
              input de email/WhatsApp (sin max-width propio) no se achica por
              debajo de su piso intrínseco del navegador — en 320-375px el
              botón "Enviar" queda empujado fuera de cada mitad. */}
          <div className="d-flex justify-content-between flex-wrap gap-2 mb-3">
            <div className="flex-fill w-100">
              <div className="d-flex align-items-center gap-1 form-control form-control-sm">
                <label htmlFor="prescriptionEmailAddress" className="text-secondary">{t('ehr:email_address', 'Email:', { context: 'form' })}</label>
                <input
                  disabled={!!email.status}
                  id="prescriptionEmailAddress"
                  type="text"
                  className="border-0 flex-fill px-0 focus-0"
                  value={email.address || ''}
                  onChange={e => setEmail({ ...email, address: e.target.value })}
                />
                <button className={`btn btn-sm p-0 ${email.status === 'sent' ? 'border-0 text-success' : 'btn-link'}`} onClick={sendEmail} disabled={!!email.status}>
                  {email.status === 'sent' ? (
                    t('ehr:sending', 'Enviado', { context: 'form' })
                  ) : email.status === 'sending' ? (
                    t('ehr:sending', 'Enviando', { context: 'form' })
                  ) : (
                    t('ehr:send', 'Enviar', { context: 'form' })
                  )}
                </button>
              </div>
              {Array.isArray(resource?.emails) && resource.emails.length > 0 && (
                <div className="btn btn-sm bg-light text-truncate text-start" data-tooltip-id="app-tooltip" data-tooltip-content={t('ehr:prescription_cc1_tip', `Enviarme copia. ${resource.emails.map(e => e.value).join(', ')}`, { emails: resource.emails.map(e => e.value).join(', ') })}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    checked={!!email.cc1}
                    id="prescriptionCC1"
                    onChange={() => setEmail({ ...email, cc1: !email.cc1 })}
                  />
                  <label className="form-check-label ms-1" htmlFor="prescriptionCC1">
                    {t('ehr:prescription_form_email_cc1', 'Copia')}
                  </label>
                </div>

              )}
              {Array.isArray(location?.emails) && location.emails.length > 0 && (
                <div className="btn btn-sm bg-light text-truncate text-start" data-tooltip-id="app-tooltip" data-tooltip-content={t('ehr:prescription_cc2_tip', `Enviar copia al consultorio. ${location.emails.join(', ')}`, { emails: location.emails.join(', ') })}>
                  <input
                    className="form-check-input"
                    type="checkbox"
                    value=""
                    checked={!!email.cc2}
                    id="prescriptionCC2"
                    onChange={() => setEmail({ ...email, cc2: !email.cc2 })}
                  />
                  <label className="form-check-label ms-1" htmlFor="prescriptionCC2">
                    {t('ehr:prescription_form_email_cc2', 'Copia consultorio')}
                  </label>
                </div>
              )}
            </div>
            <div className="flex-fill w-100">
              <div className="d-flex align-items-center gap-1 form-control form-control-sm">
                <label htmlFor="prescriptionWhatsapp" className="text-secondary">{t('ehr:whatsapp_phone_number', 'WhatsApp:')}</label>
                <input
                  id="prescriptionWhatsapp"
                  type="text"
                  className="border-0 flex-fill px-0 focus-0"
                  value={whatsapp.number || ''}
                  onChange={e => setWhatsApp({ ...whatsapp, number: e.target.value })}
                />
                <a className="btn btn-sm btn-link p-0" target="_blank" rel="noopener noreferrer" href={`https://api.whatsapp.com/send?phone=${whatsapp.number}&${whatsapp.params || ''}`}>
                  {t('ehr:send', 'Enviar', { context: 'form' })}
                </a>
              </div>
            </div>
          </div>
          <div className="d-flex align-items-center justify-content-between">
            <div>
              <button className="btn btn-sm link-primary" onClick={() => reset(type || 'prescription', 'edit')}>{t('ehr:edit_prescription', 'Editar')}</button>
              <button className="btn btn-sm link-primary" onClick={() => reset('prescription')}>{t('ehr:new_prescription', 'Nueva Receta')}</button>
              <button className="btn btn-sm link-primary" onClick={() => reset('order')}>{t('ehr:new_order', 'Nueva Orden')}</button>
            </div>

            <button className="btn btn-sm link-secondary" onClick={onClose}>{t('close')}</button>
          </div>

        </div>
      </div>
    </div>
  ) : (
    <div className={props.className || ''}>
      <div className="d-flex justify-content-center align-items-center align-items-center gap-2 mt-3">
        <div style={{ maxWidth: '680px' }} className="d-flex flex-fill flex-column gap-2 bg-white rounded over p-3">

          <Resource
            teamData={team?.data}
            resource={resource}
            location={location}
            license={license}
            identification={identification}
            onChangeLicense={onChangeLicense}
            onChangeLocation={onChangeLocation}
            onChangeSpecialty={onChangeSpecialty}
            onChangeIdentification={onChangeIdentification}
            onWarnings={setResourceWarnings}
          />
          {!consumer && (
            <Consumer.Selector
              onChange={consumer => setConsumer(clone(consumer))}
              warnings={warnings}
            />
          )}
          <Consumer
            consumer={consumer}
            financier={financier}
            onChangeFinancier={onChangeFinancier}
            onClickEdit={() => setShowConsumerForm(true)}
            onWarnings={setConsumerWarnings}
            footer={
              isCustomFinancier ? (
                <div className="text-danger small mt-2 pt-2 border-top">
                  {t('prescription_custom_financier_warning', 'Tenés seleccionada una cobertura personalizada, la receta podría no integrarse. Selecciona una cobertura válida.')} <span className="link-danger fw-bold text-decoration-underline" onClick={() => setShowConsumerForm(true)}>{t('edit', 'Editar')}</span>
                </div>
              ) : null
            }
          />
          <div>
            <input
              disabled={false}
              placeholder={t('ehr:prescriptions_form_placeholder_date', 'Fecha')}
              className="form-control"
              value={state.date || ''}
              onChange={onChange}
              name="date"
              type="date"
            />
          </div>
          {type === 'prescription' ? (
            <>
              <div className="position-relative">
                <Vademecum
                  placeholder={t('ehr:prescription_form_placeholder_vademecum', 'Buscador de tratamientos')}
                  onChange={onVademecumChange}
                  onChangeReset
                />
                {current.length > 0 && (
                  <div className="position-absolute" style={{ top: '5px', right: '5px' }}>
                    {/* Bug real (auditoría 2026-08-24, responsive): sin
                        left ni max-width, un elemento position-absolute usa
                        shrink-to-fit basado en su contenido — text-truncate
                        (white-space:nowrap) no acota nada por sí solo, así
                        que en 320-375px el checkbox+label+contador podía
                        extenderse más allá del borde izquierdo del
                        buscador. */}
                    <div className="text-truncate btn btn-sm p-0 btn-link" style={{ maxWidth: 'min(320px, calc(100vw - 40px))' }}>
                      <input
                        className="form-check-input"
                        type="checkbox"
                        value=""
                        checked={!!showCurrentTreatments}
                        id="showCurrentTreatments"
                        name="showCurrentTreatments"
                        onChange={() => setShowCurrentTreatments(!showCurrentTreatments)}
                      />
                      <label className="form-check-label ms-1" htmlFor="showCurrentTreatments">
                        {t('ehr:prescription_form_show_ehr_treatments_label', 'Mostrar los tratamientos de HCE')} <sup className="text-secondary">{current.length}</sup>
                      </label>
                    </div>
                  </div>
                )}
                {checkingProducts && (
                  <div className="position-absolute" style={{ top: '5px', left: '5px' }}>
                    <div className="spinner-border spinner-border-sm text-primary" role="status">
                      <span className="visually-hidden">{t('loading')}</span>
                    </div>
                  </div>
                )}
              </div>
              {_treatments.length > 0 && (
                <div>
                  {_treatments.map(treatment =>
                    <Treatment
                      treatment={treatment}
                      onChange={onChangeTreatment}
                      key={treatment.product_code}
                    />
                  )}
                </div>
              )}
              {showCurrentTreatments && current.length > 0 && (
                <div>
                  {current.map(treatment =>
                    <Treatment
                      treatment={{
                        ...treatment,
                        ...state.treatments.find(t => t.product_code === treatment.product_code)
                      }}
                      onChange={onChangeTreatment}
                      key={treatment.product_code}
                    />
                  )}
                </div>
              )}

              <div>
                <Textarea
                  disabled={false}
                  placeholder={t('ehr:prescriptions_form_placeholder_diagnostic', 'Diagnostico')}
                  className={`form-control${state.treatments.length > 0 && warnings.diagnostic ? ' is-invalid bg-danger-light' : ''}`}
                  value={state.diagnostic || ''}
                  onChange={onChange}
                  name="diagnostic"
                  minRows="1"
                />
                {state.treatments.length > 0 && warnings.diagnostic && (
                  <small className="text-danger ms-2 text-center">{t('ehr:prescriptions_diagnostic_warning', 'El diagnostico es obligatorio')}</small>
                )}
              </div>

              <div>
                <Textarea
                  disabled={false}
                  placeholder={t('ehr:prescriptions_form_placeholder_indications', 'Indicaciones (opcional)')}
                  className="form-control"
                  value={state.indications || ''}
                  onChange={onChange}
                  name="indications"
                  minRows="1"
                />
              </div>

              <div>
                <Textarea
                  disabled={false}
                  placeholder={t('ehr:prescriptions_form_placeholder_content', 'Comentarios (opcional)')}
                  className="form-control"
                  value={state.content || ''}
                  onChange={onChange}
                  name="content"
                  minRows="1"
                />
              </div>
            </>
          ) : (
            <>
              <div>
                <Textarea
                  disabled={false}
                  placeholder={t('ehr:prescriptions_form_placeholder_content_order', 'Descripcion de la orden o estudio')}
                  className="form-control"
                  value={state.content || ''}
                  onChange={onChange}
                  name="content"
                  minRows="4"
                />
              </div>
            </>
          )}

          <div className="p-2 border rounded">
            {/* Bug real (auditoría 2026-08-18, responsive): sin flex-wrap/gap,
                cuando ya hay una firma guardada (signature.canvas==='hide' &&
                signature.base64 -- el caso normal para un profesional con una
                sesión previa) el checkbox+label "Firma manuscrita" + la
                preview de la imagen + el botón "Cambiar firma" suman más
                ancho que los ~327px disponibles a 375px, y el overflow-x:hidden
                global recorta "Cambiar firma". */}
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
              <div className="btn bg-light text-truncate text-start">
                <input
                  className="form-check-input"
                  type="checkbox"
                  value=""
                  checked={!!showCanvasSignature}
                  id="prescriptionCanvasSignature"
                  onChange={() => setShowCanvasSignature(!showCanvasSignature)}
                />
                <label className="form-check-label ms-1" htmlFor="prescriptionCanvasSignature">
                  {t('ehr:prescription_form_canvas_signature_label', 'Firma manuscrita')}
                </label>
              </div>
              {signature.canvas === 'hide' && !!signature.base64 && (
                <div className="flex-fill">
                  <img src={signature.base64} alt="" height="30" className="d-block" />
                </div>
              )}
              {showCanvasSignature && signature.canvas !== 'hide' ? (
                <button className="btn btn-sm text-secondary" onClick={onClearSignature}>{t('ehr:prescription_form_clear_canvas_signature_1', 'Borrar firma')}</button>
              ) : showCanvasSignature && signature.canvas === 'hide' && (
                <>
                  <button className="btn btn-sm text-secondary" onClick={onClearSignature}>
                    {t('ehr:prescription_form_clear_canvas_signature_2', 'Cambiar firma')}
                  </button>
                </>
              )}
            </div>
            <div className="position-relative">
              {!signature?.base64 && showCanvasSignature && (
                <div className="bg-light mt-2 rounded" style={{
                  position: 'absolute',
                  top: 0,
                  width: '100%',
                  height: '150px',
                  textAlign: 'center',
                  color: 'rgba(0, 0, 0, 0.5)',
                  fontSize: '35px',
                  fontWeight: '200',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>{t('ehr:prescription_form_canvas_signature_hint', 'Dibuje su firma aqui')}</div>
              )}
              <SignatureCanvas
                onEnd={onUpdateSignature}
                onBegin={onUpdateSignature}
                penColor="blue"
                maxWidth={4}
                minWidth={2.5}
                canvasProps={{
                  // width/height acá son el fallback antes de que corra el
                  // efecto de resize de arriba (que iguala la resolución real
                  // al tamaño CSS ya resuelto) — el style es lo que hace que
                  // el canvas realmente ocupe el ancho disponible en mobile.
                  width: 630,
                  height: 150,
                  style: { width: '100%', maxWidth: '630px', height: '150px' },
                  className: showCanvasSignature && signature.canvas !== 'hide' ? `mt-2 rounded position-relative ${signature ? 'bg-light' : ''}` : 'd-none'
                }}
                ref={$canvas}
              />
            </div>
          </div>

          <div className="p-2 border rounded">
            {/* Bug real (auditoría 2026-08-24, responsive): checkbox "Firma
                digital" + label condicional (maxWidth:180px) + input flex-fill
                sin flex-wrap — mismo patrón ya corregido en la fila hermana
                de "Firma manuscrita" (línea ~1101, auditoría 2026-08-18),
                esta quedó afuera. */}
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <div className="btn bg-light text-truncate">
                <input
                  className="form-check-input"
                  type="checkbox"
                  value=""
                  checked={!!showCertificateForm}
                  id="prescriptionCertificateSignature"
                  onChange={() => setShowCertificateForm(!showCertificateForm)}
                />
                <label className="form-check-label ms-1" htmlFor="prescriptionCertificateSignature">
                  {t('ehr:prescription_form_certificate_signature_label', 'Firma digital')}
                </label>
              </div>

              {showCertificateForm && (
                <>
                  <div>
                    {certificate.name ? (
                      <label className="btn bg-light text-truncate" style={{ maxWidth: '180px' }} htmlFor="certificateFile">
                        <IconFileEarmarkFill className="text-secondary" /> <strong>{certificate.name}</strong>
                      </label>
                    ) : (
                      <label className="btn bg-light text-truncate" style={{ maxWidth: '180px' }} htmlFor="certificateFile">
                        {t('ehr:prescription_form_certificate_signature_select_file', 'Seleccionar certificado')}
                      </label>
                    )}
                    <input
                      type="file"
                      name="file"
                      className="form-control"
                      id="certificateFile"
                      onChange={onChangeCertificate}
                      accept=".p12,.pem,.crt,.cert,.cer,.x509,application/x-pkcs12"
                    />
                  </div>

                  <div
                    className="flex-fill position-relative"
                    data-tooltip-id="app-tooltip"
                    data-tooltip-content={t('ehr:prescription_form_certificate_signature_empty_phrase_message', 'Si el certificado no tiene clave deje en blanco el campo clave')}
                  >
                    <input
                      className="form-control"
                      type="text"
                      name="phrase"
                      id="certificatePhrase"
                      value={certificate.phrase || ''}
                      onChange={onChangeCertificate}
                      placeholder={t('ehr:prescription_form_certificate_signature_phrase_label', 'Clave del certificado')}
                    />
                    <div
                      className="position-absolute text-secondary"
                      style={{ top: 5, right: 8 }}
                    >
                      <IconInfoCircle width="14" />
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="p-2 border rounded">
            {/* Bug real (auditoría 2026-08-24, responsive): checkbox "Incluir
                logo" + label condicional (maxWidth:180px) + preview sin
                flex-wrap — mismo patrón ya corregido en la fila hermana de
                "Firma manuscrita" (línea ~1101, auditoría 2026-08-18), esta
                quedó afuera. */}
            <div className="d-flex gap-2 align-items-center flex-wrap">
              <div className="btn bg-light text-truncate" data-tooltip-id="app-tooltip" data-tooltip-content={t('ehr:prescription_logo_tip', `Logo debe ser apaisado, es decir debe ser mas largo que ancho. La resolucion minima recomendada es de 800x286. El ratio del ancho sobre el largo es de 2.8, es decir el largo debe ser 2.8 veces el ancho. Ejemplo de dimensiones validas
800x286
1024x365
2048x731`)}>
                <input
                  className="form-check-input"
                  type="checkbox"
                  value=""
                  checked={!!logo.flag}
                  id="prescriptionLogoFlag"
                  name="flag"
                  onChange={onChangeLogo}
                />
                <label className="form-check-label ms-1" htmlFor="prescriptionLogoFlag">
                  {t('ehr:prescription_form_logo_label', 'Incluir logo')}
                </label>
              </div>
              {logo.flag && (
                <>
                  {logo.uploading ? (
                    <label className="btn text-truncate text-secondary" style={{ maxWidth: '180px' }}>
                      {t('ehr:prescription_form_logo_uploading', 'Aguarde...')}
                    </label>
                  ) : logo.url ? (
                    <label className="btn bg-light text-truncate" style={{ maxWidth: '180px' }} htmlFor="logoFile">
                      {t('ehr:prescription_form_logo_change_file', 'Cambiar logo')}
                    </label>
                  ) : (
                    <label className="btn bg-light text-truncate" style={{ maxWidth: '180px' }} htmlFor="logoFile">
                      {t('ehr:prescription_form_logo_select_file', 'Seleccionar logo')}
                    </label>
                  )}
                </>
              )}

              <div>
                <input
                  type="file"
                  name="file"
                  className="form-control"
                  id="logoFile"
                  ref={$logo}
                  onChange={onChangeLogo}
                  accept=".jpg,.jpeg,.png"
                />
              </div>

              {logo.url && (
                <div className="flex-fill d-flex justify-content-between">
                  <div>
                    <img src={logo.url} alt="" height="30" className="d-block" />
                  </div>
                  <div>
                    <button className="btn btn-sm text-secondary" onClick={onClearLogo}>
                      {t('ehr:prescription_form_clear_logo', 'Borrar logo')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Bug real (auditoría 2026-08-18, responsive): dos bugs en esta
              fila. (1) "align-items center" sin guion no es una clase de
              Bootstrap válida (queda como "align-items" + "center", ninguna
              existe) -- align-items:center nunca se aplicaba. (2) sin
              flex-wrap, los 6 controles (Sin copia/Duplicado/Triplicado +
              A4/A5/HIV) suman bastante más que los ~343px disponibles a
              375px -- el overflow-x:hidden global recorta A4/A5 y el
              checkbox de receta anónima (HIV), un dato sensible. */}
          <div className="d-flex align-items-center flex-wrap gap-2">
            <div className="btn btn-sm bg-light border text-secondary">
              <input
                className="form-check-input"
                type="radio"
                value=""
                checked={parseInt(copies) === 1}
                id="prescriptionCopies1"
                onChange={() => setCopies(1)}
              />
              <label className="form-check-label ms-1" htmlFor="prescriptionCopies1">
                {t('ehr:prescription_copies_1', 'Sin copia')}
              </label>
            </div>

            <div className="btn btn-sm bg-light border text-secondary">
              <input
                className="form-check-input"
                type="radio"
                value=""
                checked={parseInt(copies) === 2}
                id="prescriptionCopies2"
                onChange={() => setCopies(2)}
              />
              <label className="form-check-label ms-1" htmlFor="prescriptionCopies2">
                {t('ehr:prescription_copies_2', 'Duplicado')}
              </label>
            </div>
            <div className="btn btn-sm bg-light border text-secondary">
              <input
                className="form-check-input"
                type="radio"
                value=""
                checked={parseInt(copies) === 3}
                id="prescriptionCopies3"
                onChange={() => setCopies(3)}
              />
              <label className="form-check-label ms-1" htmlFor="prescriptionCopies3">
                {t('ehr:prescription_copies_3', 'Triplicado')}
              </label>
            </div>
            <div className="flex-fill"></div>

            <div className="btn btn-sm bg-light border text-secondary" data-tooltip-id="app-tooltip" data-tooltip-content={t('ehr:prescription_size_A4_tip', 'Tamañano de impresion hoja A4')}>
              <input
                className="form-check-input"
                type="radio"
                value=""
                checked={size === 'A4'}
                id="prescriptionSizeA4"
                onChange={() => setSize('A4')}
              />
              <label className="form-check-label ms-1" htmlFor="prescriptionSizeA4">
                {t('ehr:prescription_size_A4', 'A4')}
              </label>
            </div>

            <div className="btn btn-sm bg-light border text-secondary" data-tooltip-id="app-tooltip" data-tooltip-content={t('ehr:prescription_size_A5_tip', 'Tamañano de impresion hoja A5')}>
              <input
                className="form-check-input"
                type="radio"
                value=""
                checked={size === 'A5'}
                id="prescriptionSizeA5"
                onChange={() => setSize('A5')}
              />
              <label className="form-check-label ms-1" htmlFor="prescriptionSizeA5">
                {t('ehr:prescription_size_A5', 'A5')}
              </label>
            </div>

            <div className="btn btn-sm bg-light border text-secondary ms-2" data-tooltip-id="app-tooltip" data-tooltip-content={t('ehr:prescription_anonymous_tip', 'Genera recetas anonimas. Ejemplo para pacientes con HIV')}>
              <input
                className="form-check-input"
                type="checkbox"
                value=""
                checked={!!state.tags?.anonymous}
                id="prescriptionAnonymous"
                onChange={() => setState({ ...state, tags: { ...state.tags, anonymous: !state.tags?.anonymous } })}
              />
              <label className="form-check-label ms-1" htmlFor="prescriptionAnonymous">
                {t('ehr:prescription_anonymous', 'HIV')}
              </label>
            </div>
          </div>

          <div className="d-flex gap-2 justify-content-end justify-content-md-between">
            <button
              className="btn btn-sm btn-link"
              onClick={() => save()}
              disabled={mutation.isLoading
                || ((warnings.treatments && warnings.indications)
                  || state.treatments && state.treatments.length > 0 && warnings.diagnostic
                  || warnings.content)}
            >
              {!!mutation.isLoading && (
                <div className="spinner-border spinner-border-sm text-primary">
                  <span className="visually-hidden">{t("loading")}</span>
                </div>
              )} {t('ehr:create_prescription', 'Generar', { context: 'form' })}
            </button>
            {props.user?.isStaff && (
              <Link className="btn btn-sm text-secondary" to={new URLSearchParams(_location.search).get('preview') ? _location.pathname : `${_location.pathname}?preview=true`}>{t('preview', 'Preview')}</Link>
            )}
            <button className="btn btn-sm link-secondary" onClick={onClose}>{t('close')}</button>
          </div>

          {response?.status?.description && (
            <div className="text-center">
              <small className="text-danger me-2 fw-bold">
                {t('ehr:prescription_error', 'ERROR:', { context: 'form' })}
              </small>
              <small className="text-danger">
                {response.status?.description}
              </small>
            </div>
          )}

          {(warnings.treatments && warnings.indications) || (state.treatments && state.treatments.length > 0 && warnings.diagnostic) ? (
            <small className="text-danger ms-2 text-center">
              {t('ehr:prescription_warnings_missing_info_1', 'Debe agregar al menos un tratamiento o dar una indicacion', { context: 'form' })}
            </small>
          ) : warnings.content && (
            <small className="text-danger ms-2 text-center">
              {t('ehr:prescription_warnings_missing_info_2', 'Para generar la orden debe escribir el detalle de la misma', { context: 'form' })}
            </small>
          )}
        </div>
        {/* Bug real (auditoría 2026-08-18, responsive): el width:1132 fijo
            (pensado para mostrar la hoja completa en desktop) desborda 757px
            a 375px -- con el overflow-x:hidden global de la app, quedaba
            prácticamente invisible en mobile (solo se veía una esquina del
            iframe), sin ninguna forma de recuperar el resto. El wrapper de
            afuera con overflowX:auto deja pannear/scrollear hasta ver el
            resto del preview en vez de recortarlo silenciosamente; en
            desktop (ancho disponible > 1132px) no cambia nada, no hace falta
            scrollear. */}
        {_location.search.includes('preview=true') && preview && (
          <div style={{ overflowX: 'auto' }}>
            <div style={{ height: 857, width: 1132 }}>
              <iframe src={preview} className="w-100 h-100" />
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Prescription;
